// Copyright 2026 Google LLC
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
/**
 * Handwritten extension of the generated Project class.
 *
 * Adds methods that cannot be generated by the domain-map pipeline because
 * they rely on private REST endpoints or complex filesystem orchestration
 * with no corresponding MCP tool in tools-manifest.json.
 *
 * The generated Project class is re-exported from this module, so consumers
 * import a single Project class that includes both generated and handwritten
 * methods.
 */
import { Project as GeneratedProject } from '../generated/src/project.js';
import { StitchError } from './spec/errors.js';
import { DownloadAssetsHandler } from './download-handler.js';
import { DownloadAssetsInputSchema } from './spec/download.js';
import { UploadImageInputSchema, } from './spec/upload.js';
import { UploadImageHandler } from './upload-handler.js';
export class Project extends GeneratedProject {
    /**
     * Upload an image file to the project and create a new Screen from it.
     *
     * WHY THIS IS NOT GENERATED:
     *   BatchCreateScreens is a private REST endpoint — it has no MCP tool
     *   in tools-manifest.json. It also requires reading a file from disk and
     *   base64-encoding it, which the codegen arg-routing model cannot express.
     *
     * @param filePath - Absolute or relative path to the image (PNG, JPG, JPEG, WEBP).
     * @param opts - Optional screen title and createScreenInstances flag.
     * @returns An array of Screen objects created from the upload.
     * @throws {StitchError} on file not found, unsupported format, or upload failure.
     *
     * @example
     * const [screen] = await project.uploadImage('./mockup.png', { title: 'Home Screen' });
     * const html = await screen.getHtml();
     */
    async uploadImage(filePath, opts) {
        const input = UploadImageInputSchema.parse({ filePath, ...opts });
        const handler = new UploadImageHandler(this.client);
        const result = await handler.execute(this.projectId, input);
        if (!result.success) {
            throw new StitchError({
                code: result.error.code === 'FILE_NOT_FOUND'
                    ? 'NOT_FOUND'
                    : result.error.code === 'AUTH_FAILED'
                        ? 'AUTH_FAILED'
                        : 'UNKNOWN_ERROR',
                message: result.error.message,
                recoverable: result.error.recoverable,
            });
        }
        return result.screens;
    }
    /**
     * Downloads all screens and their referenced assets to a local directory.
     * Rewrites URLs in HTML to be self-contained.
     *
     * WHY THIS IS NOT GENERATED:
     *   Requires complex filesystem orchestration (atomic renames, temp dirs,
     *   hash-based filenames, URL rewriting) that the codegen pipeline cannot
     *   express.
     *
     * @param outputDir - The directory to save assets to.
     * @param opts - Optional file mode, temp directory, and assets subdirectory.
     * @returns A trace array describing each downloaded screen and its file path.
     * @throws {StitchError} on project not found, network failure, or path traversal.
     */
    async downloadAssets(outputDir, opts) {
        const handler = new DownloadAssetsHandler(this.client);
        const input = DownloadAssetsInputSchema.parse({
            projectId: this.projectId,
            outputDir,
            ...opts,
        });
        const result = await handler.execute(input);
        if (!result.success) {
            let code = 'UNKNOWN_ERROR';
            switch (result.error.code) {
                case 'PROJECT_NOT_FOUND':
                    code = 'NOT_FOUND';
                    break;
                case 'FETCH_FAILED':
                    code = 'NETWORK_ERROR';
                    break;
                case 'PATH_TRAVERSAL_ATTEMPT':
                    code = 'VALIDATION_ERROR';
                    break;
            }
            throw new StitchError({
                code,
                message: result.error.message,
                recoverable: result.error.recoverable,
            });
        }
        return {
            screens: result.downloadedScreens,
            warnings: result.warnings ?? [],
        };
    }
}
//# sourceMappingURL=project-ext.js.map